import {useCallback, useEffect, useRef, useState} from 'react';
import {preferenceKey, readPreferences} from './preferences.mjs';
import type {ScenePreferences, SceneState} from './types';
import {resolveDesktopSceneURL, type DesktopSceneBridge} from './desktopScene.mjs';
import {createSceneChannel} from './channel.mjs';

const protocol = 'skyview-scene-v1';
type Options = Omit<SceneState, 'preferences'> & {
  userKey: string;
  refreshAuth: () => Promise<unknown>;
};

export function useSceneWindow(options: Options) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const latest = useRef(options); latest.current = options;
  const session = useRef<{
    popup: Window; target: Window | null; origin: string; channel: string;
    stop: () => void; key: string;
  } | null>(null);
  const key = preferenceKey(options.apiBase, options.userKey);
  const preferences = useRef<ScenePreferences>({version: 1});

  const send = useCallback(() => {
    const current = session.current;
    if (!current?.target || current.popup.closed) return;
    const {userKey: _, refreshAuth: __, ...state} = latest.current;
    current.target.postMessage({protocol, channel: current.channel, type: 'state', state: {...state, preferences: preferences.current}}, current.origin);
  }, []);
  const close = useCallback(() => {
    const current = session.current; session.current = null;
    current?.stop();
    try {current?.target?.postMessage({protocol, channel: current.channel, type: 'close'}, current.origin); current?.popup.close();} catch { /* Already closed. */ }
    setOpen(false); setReady(false);
  }, []);

  const toggle = useCallback(() => {
    if (session.current) {close(); return;}
    const channel = createSceneChannel();
    const desktop = !!(window as Window & {runtime?: unknown}).runtime || window.location.hostname === 'wails.localhost';
    const base = new URL(import.meta.env.BASE_URL, window.location.origin);
    const url = new URL('scene-window.html', base);
    url.searchParams.set('channel', channel);
    url.searchParams.set('hostOrigin', window.location.origin);
    const popup = window.open(desktop ? 'about:blank' : url.href, `skyview-scene-${channel}`, 'width=1200,height=800,resizable=yes');
    if (!popup) {setError('The 3D window was blocked. Allow popups for SkyView and try again.'); return;}
    setError(''); setOpen(true);
    try {preferences.current = readPreferences(localStorage.getItem(key));} catch {preferences.current = {version: 1};}
    const current = {popup, target: desktop ? null : popup, origin: url.origin, channel, key, stop: () => {}};
    session.current = current;
    let initialized = false;
    const listener = (event: MessageEvent) => {
      const data = event.data;
      if (session.current !== current || event.source !== current.target || event.origin !== current.origin || data?.protocol !== protocol || data.channel !== channel) return;
      if (data.type === 'ready') {initialized = true; setReady(true); send();}
      if (data.type === 'save') {
        const value = readPreferences(data.preferences);
        try {localStorage.setItem(key, JSON.stringify(value)); preferences.current = value; send();}
        catch {current.target?.postMessage({protocol, channel, type: 'error', error: 'Unable to save 3D preferences in this browser.'}, current.origin);}
      }
      if (data.type === 'refresh-auth') void latest.current.refreshAuth().then(send).catch(() => setError('Please sign in again to load 3D assets.'));
      if (data.type === 'closed') close();
    };
    window.addEventListener('message', listener);
    const storage = (event: StorageEvent) => {if (event.key === key) {preferences.current = readPreferences(event.newValue); send();}};
    window.addEventListener('storage', storage);
    const timer = window.setInterval(() => {if (popup.closed) close();}, 500);
    const timeout = window.setTimeout(() => {if (!initialized && session.current === current) setError('The 3D window could not finish loading. Close it and try again.');}, 30000);
    current.stop = () => {if (desktop) popup.removeEventListener('message', listener); window.removeEventListener('message', listener); window.removeEventListener('storage', storage); clearInterval(timer); clearTimeout(timeout);};
    if (desktop) {
      const initializeDesktopPopup = async () => {
        // Complete the blank document synchronously, as the Map window does.
        // Otherwise WebView2's pending about:blank navigation can replace it while
        // the backend lookup is in flight (losing its body and message listener).
        popup.document.open();
        popup.document.write('<!doctype html><html><head><meta charset="utf-8"><title>SkyView 3D</title></head><body></body></html>');
        popup.document.close();
        popup.addEventListener('message', listener);
        popup.document.body.style.cssText = 'margin:0;height:100vh;background:#111827;color:white';
        popup.document.body.textContent = 'Loading 3D view…';
        const bridge = (window as Window & {go?: {main?: {DesktopApp?: DesktopSceneBridge}}}).go?.main?.DesktopApp;
        const sceneURL = await resolveDesktopSceneURL(bridge);
        if (session.current !== current || popup.closed) return;
        sceneURL.search = url.search;
        const frame = popup.document.createElement('iframe');
        frame.style.cssText = 'border:0;width:100%;height:100%;display:block';
        frame.title = '3D View'; frame.src = sceneURL.href;
        popup.document.body.replaceChildren(frame);
        current.target = frame.contentWindow; current.origin = sceneURL.origin;
      };
      void initializeDesktopPopup().catch(cause => {
        if (session.current !== current) return;
        clearTimeout(timeout);
        const detail = cause instanceof Error ? cause.message : String(cause);
        const message = `Unable to initialize the desktop 3D window: ${detail}. Close it and try again.`;
        setError(message);
        try {popup.document.body.textContent = message;} catch { /* The user may have closed the popup. */ }
      });
    }
  }, [close, key, send]);

  useEffect(() => {send();}, [options.sources, options.theme, options.accessToken, options.admin, options.apiBase, send]);
  useEffect(() => {if (session.current && session.current.key !== key) close();}, [key, close]);
  useEffect(() => {window.addEventListener('beforeunload', close); return () => {window.removeEventListener('beforeunload', close); close();};}, [close]);
  return {open, ready, error, toggle, dismissError: () => setError('')};
}
